import React, { useState } from 'react';
import { ValidationCheckResult } from './importDetails/contentValidation';
import './ImportDetails.css'; // Reuse existing styles

interface ValidationDisplayProps {
    checks: ValidationCheckResult[];
    isValid: boolean;
    showEmailAction?: boolean; // Optional: show email action for issues
}

// Helper function to create mailto link
const createMailtoLink = (email: string, issue: any, checkName: string): string => {
    const subject = encodeURIComponent('Fehler in den Tablerworld Daten');
    const body = encodeURIComponent(
        `Hallo,\n\n` +
        `es wurde ein Problem in den Tablerworld-Daten für Ihren Club "${issue.clubName}" festgestellt:\n\n` +
        `Prüfung: ${checkName}\n` +
        `Spalte: ${issue.column}\n` +
        `Wert: ${issue.value}\n` +
        `Problem: ${issue.message}\n\n` +
        `Bitte korrigiere diese Informationen in Tablerworld.\n\n` +
        `Mit freundlichen Grüßen`
    );
    return `mailto:${email}?subject=${subject}&body=${body}`;
};

const ValidationDisplay: React.FC<ValidationDisplayProps> = ({ checks, isValid, showEmailAction = true }) => {
    // Initialize with all checks expanded by default
    const [expandedChecks, setExpandedChecks] = useState<Set<string>>(
        new Set(checks.map(check => check.checkName))
    );

    const toggleCheck = (checkName: string) => {
        setExpandedChecks(prev => {
            const newSet = new Set(prev);
            if (newSet.has(checkName)) {
                newSet.delete(checkName);
            } else {
                newSet.add(checkName);
            }
            return newSet;
        });
    };

    if (checks.length === 0) {
        return null;
    }

    return (
        <div className="content-validation-card">
            <div className="validation-header">
                {isValid ? (
                    <>
                        <span className="validation-icon success">✓</span>
                        <h3>Datenqualität: Keine Probleme gefunden</h3>
                    </>
                ) : (
                    <>
                        <span className="validation-icon warning">⚠</span>
                        <h3>Datenqualität: Probleme gefunden</h3>
                    </>
                )}
            </div>
            
            <div className="validation-details">
                <div className="validation-checks">
                    {checks.map((check, index) => (
                        <div 
                            key={index} 
                            className={`validation-check ${check.isValid ? 'valid' : 'invalid'}`}
                        >
                            <div 
                                className="validation-check-header"
                                onClick={() => toggleCheck(check.checkName)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="validation-check-info">
                                    <span className={`check-icon ${check.isValid ? 'success' : 'error'}`}>
                                        {check.isValid ? '✓' : '✗'}
                                    </span>
                                    <div className="check-text">
                                        <h4>{check.checkName}</h4>
                                        <p className="check-description">{check.checkDescription}</p>
                                    </div>
                                </div>
                                <div className="check-summary">
                                    {check.isValid ? (
                                        <span className="status-text success-text">Alle Einträge gültig</span>
                                    ) : (
                                        <span className="status-text error-text">
                                            {check.issues.length} Problem{check.issues.length !== 1 ? 'e' : ''} gefunden
                                        </span>
                                    )}
                                    <span className="expand-icon">
                                        {expandedChecks.has(check.checkName) ? '▼' : '▶'}
                                    </span>
                                </div>
                            </div>
                            
                            {expandedChecks.has(check.checkName) && !check.isValid && (
                                <div className="validation-check-issues">
                                    <table className="issues-table">
                                        <thead>
                                            <tr>
                                                <th>Club</th>
                                                <th>Spalte</th>
                                                <th>Wert</th>
                                                <th>Problem</th>
                                                {showEmailAction && <th>Aktion</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {check.issues.map((issue, issueIdx) => {
                                                const treasurerEmail = issue.rowData?.['[Treasurer] Email'];
                                                return (
                                                    <tr key={issueIdx}>
                                                        <td>{issue.clubName}</td>
                                                        <td>{issue.column}</td>
                                                        <td className="issue-value">{String(issue.value)}</td>
                                                        <td>{issue.message}</td>
                                                        {showEmailAction && (
                                                            <td style={{ textAlign: 'center' }}>
                                                                {treasurerEmail ? (
                                                                    <a 
                                                                        href={createMailtoLink(treasurerEmail, issue, check.checkName)}
                                                                        title={`E-Mail an ${treasurerEmail} senden`}
                                                                        style={{ 
                                                                            fontSize: '1.2em', 
                                                                            textDecoration: 'none',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                    >
                                                                        ✉️
                                                                    </a>
                                                                ) : (
                                                                    <span title="Keine E-Mail-Adresse verfügbar" style={{ color: '#999' }}>-</span>
                                                                )}
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                
                {isValid && (
                    <div className="validation-summary success">
                        <p>Alle Datenqualitätsprüfungen erfolgreich abgeschlossen ({checks.length} Prüfungen)</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ValidationDisplay;
